import React, {
  ComponentType,
  useEffect,
  useState
} from 'react'
import {
  Grid,
  Popup,
  Segment,
  Table
} from 'semantic-ui-react'
import { format } from 'date-fns'
import filter from 'lodash/filter'
import orderBy from 'lodash/orderBy'
import uniq from 'lodash/uniq'
// Local
import {
  API_URL,
  FORMAT,
  REVIEW
} from '../../constant'
import { api } from '../../action/api'
import { ObjectValues } from '../../types/types'

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeGrid = Grid as unknown as ComponentType<any>
const SafePopup = Popup as unknown as ComponentType<any>
const SafeTable = Table as unknown as ComponentType<any>

type DashboardDailyReviewCountRow = {
  modificationDay?: string
  reviewCode: string
  reviewCount: number
  reviewerUsername?: string
  reviewerName: string
}

/**
 * Reports the reviews completed by each reviewer user (CDI and Coder)
 */
function DailyProductivityCount(): JSX.Element {
  const [
    reportData,
    setReportData
  ] = useState<DashboardDailyReviewCountRow[] | null>(null)

  const fetchReportData = () => {
    const url = API_URL.DASHBOARD_REVIEW_DAILY
    api.get(url).then(({ data }: { data: DashboardDailyReviewCountRow[] }) => {
      setReportData(data)
    })
  }

  useEffect(() => {
    fetchReportData()
  }, [])

  const handleClickRefresh = () => {
    fetchReportData()
  }

  const renderRefresh = (): JSX.Element => (
    <SafeGrid columns={ 1 }>
      <Grid.Column textAlign="right">
        <span className="italic">
          <span>{ format(new Date(), FORMAT.TIMESTAMP) }</span>
          <span>&nbsp;</span>
          <span
            className="linkable"
            onClick={ handleClickRefresh }
          >
              Refresh
          </span>
        </span>
      </Grid.Column>
    </SafeGrid>
  )

  const renderEmpty = (): JSX.Element => (
    <div>
      <span className="italic">Sorry, no counts yet today</span>
      <div>{ renderRefresh() }</div>
    </div>
  )

  const distinctUsers = (data: DashboardDailyReviewCountRow[]): string[] => orderBy(uniq(data.map(row => row.reviewerName)))

  // const preVisitCount = (
  //   user: string,
  //   data: DashboardDailyReviewCountRow[]
  // ): number => {
  //   const withUser = filter(data, row => row.reviewerName === user)
  //   const withCode = filter(withUser, row => row.reviewCode === REVIEW.CODE.READY_FOR_PHYSICIAN ||
  //       row.reviewCode === REVIEW.CODE.READY_FOR_CDI_QA)
  //   return withCode.reduce((a, b) => a + b.reviewCount, 0)
  // }

  // const postVisitCount = (
  //   user: string,
  //   data: DashboardDailyReviewCountRow[]
  // ): number => {
  //   const withUser = filter(data, row => row.reviewerName === user)
  //   const withCode = filter(withUser, row => row.reviewCode === REVIEW.CODE.COMPLETED ||
  //       row.reviewCode === REVIEW.CODE.READY_FOR_CODER_QA)
  //   return withCode.reduce((a, b) => a + b.reviewCount, 0)
  // }

  const reviewCodeCount = (
    user: string,
    reviewCode: ObjectValues<typeof REVIEW.CODE>,
    data: DashboardDailyReviewCountRow[]
  ): number => {
    const withUser = filter(data, row => row.reviewerName === user)
    const withCode = filter(withUser, row => row.reviewCode === reviewCode)
    return withCode.reduce((a, b) => a + b.reviewCount, 0)
  }

  // the report API returns rows grouped by user and review code
  // transform these into pre visit and post visit counts
  const renderReport = (data: DashboardDailyReviewCountRow[]): JSX.Element => {
    const users = distinctUsers(data)
    return (
      <div>
        <SafeTable definition fixed>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>Pre-Visit Review</Table.HeaderCell>
              <Table.HeaderCell>Post-Visit Review</Table.HeaderCell>
              <Table.HeaderCell>Total</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            { users.map(user => {
              const cdiQa = reviewCodeCount(
                user,
                REVIEW.CODE.READY_FOR_CDI_QA,
                data
              )
              const readyForPhysician = reviewCodeCount(
                user,
                REVIEW.CODE.READY_FOR_PHYSICIAN,
                data
              )
              const coderQa = reviewCodeCount(
                user,
                REVIEW.CODE.READY_FOR_CODER_QA,
                data
              )
              const completed = reviewCodeCount(
                user,
                REVIEW.CODE.COMPLETED,
                data
              )
              const preCount = cdiQa + readyForPhysician
              const postCount = coderQa + completed
              const total = preCount + postCount
              return (
                <Table.Row key={ user }>
                  <Table.Cell>{ user }</Table.Cell>
                  <Table.Cell>
                    <SafePopup
                      content={ `Ready for CDI QA: ${cdiQa}; Ready for Physician: ${readyForPhysician}` }
                      trigger={ <span>{ preCount }</span> }
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <SafePopup
                      content={ `Ready for Coder QA: ${coderQa}; Completed: ${completed}` }
                      trigger={ <span>{ postCount }</span> }
                    />
                  </Table.Cell>
                  <Table.Cell>{ total }</Table.Cell>
                </Table.Row>
              )
            }) }
          </Table.Body>
        </SafeTable>
        <div>{ renderRefresh() }</div>
      </div>
    )
  }

  const key = (new Date()).getTime()
  return (
    <SafeSegment key={ key }>
      <h2>Daily Productivity</h2>
      <div className="m-1-top">
        How many encounters have CDIs marked Ready for Physician or
        Coders marked Complete today, by user?
      </div>
      <div className="m-1-top">
        { reportData && reportData.length > 0
          ? renderReport(reportData)
          : renderEmpty()
        }
      </div>
    </SafeSegment>
  )
}

export default DailyProductivityCount
